import React, { useState, useMemo } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Chip,
    TextField,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Divider,
    CircularProgress,
    Alert,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import {
    useAdminRoadmapAllowedUsersQuery,
    useUpdateRoadmapAllowedUsersMutation,
    AllowedUser,
} from '../../../shared/queries/admin'

interface AllowedUsersDialogProps {
    roadmapId: string
    open: boolean
    onClose: () => void
    onSuccess: () => void
    onError: () => void
}

const AllowedUsersDialog: React.FC<AllowedUsersDialogProps> = ({ roadmapId, open, onClose, onSuccess, onError }) => {
    const [searchTerm, setSearchTerm] = useState('')
    const [pendingAdd, setPendingAdd] = useState<string[]>([])
    const [pendingRemove, setPendingRemove] = useState<string[]>([])
    const [saving, setSaving] = useState(false)

    const { data, isLoading, error } = useAdminRoadmapAllowedUsersQuery(roadmapId)
    const updateMutation = useUpdateRoadmapAllowedUsersMutation()

    const roadmap = data?.data?.roadmap
    const allowedUsers = data?.data?.allowedUsers || []
    const allOrgUsers = data?.data?.allOrgUsers || []

    const allowedUserIds = useMemo(() => {
        const ids = new Set(allowedUsers.map((u) => u._id))
        pendingAdd.forEach((id) => ids.add(id))
        pendingRemove.forEach((id) => ids.delete(id))
        return ids
    }, [allowedUsers, pendingAdd, pendingRemove])

    const availableUsers = useMemo(() => {
        return allOrgUsers.filter((u) => !allowedUserIds.has(u._id))
    }, [allOrgUsers, allowedUserIds])

    const filteredAvailableUsers = useMemo(() => {
        if (!searchTerm) return availableUsers
        const term = searchTerm.toLowerCase()
        return availableUsers.filter((u) => u.name.toLowerCase().includes(term) || u.email.toLowerCase().includes(term))
    }, [availableUsers, searchTerm])

    const currentAllowedUsers = useMemo(() => {
        const users: AllowedUser[] = []
        allowedUsers.forEach((u) => {
            if (!pendingRemove.includes(u._id)) {
                users.push(u)
            }
        })
        pendingAdd.forEach((id) => {
            const user = allOrgUsers.find((u) => u._id === id)
            if (user) {
                users.push(user)
            }
        })
        return users
    }, [allowedUsers, allOrgUsers, pendingAdd, pendingRemove])

    const handleAddUser = (userId: string) => {
        if (pendingRemove.includes(userId)) {
            setPendingRemove((prev) => prev.filter((id) => id !== userId))
        } else {
            setPendingAdd((prev) => [...prev, userId])
        }
    }

    const handleRemoveUser = (userId: string) => {
        if (pendingAdd.includes(userId)) {
            setPendingAdd((prev) => prev.filter((id) => id !== userId))
        } else {
            setPendingRemove((prev) => [...prev, userId])
        }
    }

    const handleSave = async () => {
        if (pendingAdd.length === 0 && pendingRemove.length === 0) {
            onClose()
            return
        }

        setSaving(true)
        try {
            await updateMutation.mutateAsync({
                roadmapId,
                add: pendingAdd.length > 0 ? pendingAdd : undefined,
                remove: pendingRemove.length > 0 ? pendingRemove : undefined,
            })
            onSuccess()
            onClose()
        } catch (e) {
            onError()
        } finally {
            setSaving(false)
        }
    }

    const handleClose = () => {
        setPendingAdd([])
        setPendingRemove([])
        setSearchTerm('')
        onClose()
    }

    const hasChanges = pendingAdd.length > 0 || pendingRemove.length > 0

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Manage Allowed Users
                {roadmap && (
                    <Typography variant="body2" color="text.secondary">
                        {roadmap.product} - {roadmap.indication}
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error">Failed to load user data</Alert>
                ) : (
                    <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
                        {/* Current Allowed Users */}
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Allowed Users ({currentAllowedUsers.length})
                            </Typography>
                            <Box
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    maxHeight: 350,
                                    overflow: 'auto',
                                }}
                            >
                                {currentAllowedUsers.length === 0 ? (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ p: 2, textAlign: 'center' }}
                                    >
                                        No users have access
                                    </Typography>
                                ) : (
                                    <List dense>
                                        {currentAllowedUsers.map((user) => (
                                            <ListItem key={user._id}>
                                                <ListItemText primary={user.name} secondary={user.email} />
                                                <ListItemSecondaryAction>
                                                    <IconButton
                                                        edge="end"
                                                        size="small"
                                                        onClick={() => handleRemoveUser(user._id)}
                                                        color="error"
                                                    >
                                                        <RemoveIcon />
                                                    </IconButton>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        ))}
                                    </List>
                                )}
                            </Box>
                        </Box>

                        <Divider orientation="vertical" flexItem />

                        {/* Available Users */}
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Available Users ({availableUsers.length})
                            </Typography>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Search users..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ mb: 1 }}
                            />
                            <Box
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    maxHeight: 300,
                                    overflow: 'auto',
                                }}
                            >
                                {filteredAvailableUsers.length === 0 ? (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ p: 2, textAlign: 'center' }}
                                    >
                                        {searchTerm ? 'No users match your search' : 'All users already have access'}
                                    </Typography>
                                ) : (
                                    <List dense>
                                        {filteredAvailableUsers.map((user) => (
                                            <ListItem key={user._id}>
                                                <ListItemText primary={user.name} secondary={user.email} />
                                                <ListItemSecondaryAction>
                                                    <IconButton
                                                        edge="end"
                                                        size="small"
                                                        onClick={() => handleAddUser(user._id)}
                                                        color="primary"
                                                    >
                                                        <AddIcon />
                                                    </IconButton>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        ))}
                                    </List>
                                )}
                            </Box>
                        </Box>
                    </Box>
                )}

                {hasChanges && (
                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {pendingAdd.length > 0 && (
                            <Chip label={`+${pendingAdd.length} to add`} color="success" size="small" />
                        )}
                        {pendingRemove.length > 0 && (
                            <Chip label={`-${pendingRemove.length} to remove`} color="error" size="small" />
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={saving}>
                    Cancel
                </Button>
                <Button onClick={handleSave} variant="contained" disabled={saving || !hasChanges}>
                    {saving ? <CircularProgress size={20} /> : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default AllowedUsersDialog
